import { memo, useEffect, useRef } from 'react';
import './SplashScreen.keyframes.css';
import style from './SplashScreen.module.css';
import MascotImg from '../../images/common/img_mascot.svg';

const LOGO_COLOR = '#1065D9';
const STROKE_DUR = 550;   // 글자당 그리기(ms)
const STROKE_GAP = 180;   // 글자 간 간격(ms)

const SplashScreen = memo(function SplashScreen({ onComplete }) {
  const isFinishedRef = useRef(false);
  const timerRef = useRef(null);
  const svgRef = useRef(null);

  const handleCompleteSafe = () => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (onComplete) onComplete();
  };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const paths = Array.from(svg.querySelectorAll('path'));
    let raf1 = 0, raf2 = 0;
    let doneTimer = 0;

    // 채워진 완성 상태로 즉시 되돌리는 함수 (실패/멈춤 시 안전망)
    const forceFilled = () => {
      paths.forEach((p) => {
        p.style.animation = 'none';
        p.style.strokeDashoffset = '0';
        p.style.fill = LOGO_COLOR;
        p.style.opacity = '1';
      });
    };

    const start = () => {
      let anyValid = false;
      // 화면상 왼→오 순서로 정렬 (path DOM 순서 ≠ 글자 순서)
      const ordered = paths
        .map((p) => {
          let x = 0;
          try { x = p.getBBox().x; } catch (e) { x = 0; }
          return { p, x };
        })
        .sort((a, b) => a.x - b.x)
        .map((o) => o.p);

      ordered.forEach((p, i) => {
        let len = 0;
        try { len = p.getTotalLength(); } catch (e) { len = 0; }

        // 길이 측정 실패(0/NaN) → 이 path는 그냥 채워진 상태로
        if (!len || Number.isNaN(len)) {
          p.style.strokeDashoffset = '0';
          p.style.fill = LOGO_COLOR;
          return;
        }
        anyValid = true;
        p.style.strokeDasharray = String(len);
        p.style.strokeDashoffset = String(len);
        p.style.fill = 'transparent';
        // WebkitAnimation도 같이 (구형 WebView)
        const anim =
          `wb-draw ${STROKE_DUR}ms ease forwards ${i * STROKE_GAP}ms, ` +
          `wb-fill 400ms ease forwards ${i * STROKE_GAP + STROKE_DUR}ms`;
        p.style.animation = anim;
        p.style.WebkitAnimation = anim;
      });

      // 애니메이션 유효한 path가 없으면(측정 실패) 즉시 완료
      if (!anyValid) {
        forceFilled();
        handleCompleteSafe();
        return;
      }

      // 애니메이션 총 소요시간 = 마지막 글자 시작 + 그리기 + 채우기
      const total = (paths.length - 1) * STROKE_GAP + STROKE_DUR + 400;
      // 정상 종료 시점에 완료 처리 (+80ms 여유)
      doneTimer = window.setTimeout(() => {
        forceFilled();
        handleCompleteSafe();
      }, total + 80);
    };

    // 레이아웃 확정 후 실행 (WebView에서 getTotalLength 0 방지)
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(start);
    });

    // 백그라운드 복귀 시 애니메이션이 멈춰 있으면 완성 상태로
    const onVisible = () => {
      if (document.visibilityState === 'visible') forceFilled();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (doneTimer) clearTimeout(doneTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem('splashShown', 'true'); } catch (e) {}
    timerRef.current = setTimeout(() => handleCompleteSafe(), 6000);

    let wasHidden = false;
    const handleVisibilityChange = () => {
       if (document.visibilityState === 'hidden') {
        wasHidden = true;
        return;
      }
      // 실제로 백그라운드 갔다 돌아온 경우만
      if (wasHidden) {
        setTimeout(() => { if (!isFinishedRef.current) handleCompleteSafe(); }, 500);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleAnimationEnd = (e) => {
    if (e.animationName && e.animationName.includes('containerDismiss')) handleCompleteSafe();
  };

  return (
    <div className={style['splash-bg']} onAnimationEnd={handleAnimationEnd}>
      <div className={style['splash-center']}>
        <div className={style['img-box']}>
          <svg
            ref={svgRef}
            xmlns="http://www.w3.org/2000/svg"
            width="180" height="45" viewBox="0 0 180 45" fill="none"
          >
            {/* path 6개: 기존 d 값 그대로 유지. stroke만 유지하고 fill/dash는 JS가 제어 */}
            <path data-order="0" d="M42.6526 32.9546C45.8368 32.7276 47.4618 25.7975 48.101 22.225C48.9792 17.2843 48.9852 12.3974 48.9792 7.41498C48.9792 4.78634 50.8133 3.01201 53.3404 3.00604C55.6763 3.00604 57.6059 4.54737 57.7254 7.06848C58.1735 16.424 56.2319 33.6894 49.1047 39.9025C45.1617 43.3437 39.4683 43.033 35.7644 39.341C33.6794 37.262 32.365 34.7409 31.1583 31.8792C30.0411 34.9559 28.5356 37.7518 26.1041 39.8966C22.191 43.3556 16.408 43.039 12.9012 39.2095C8.71927 34.8066 6.90312 27.3627 6.007 21.293C5.30802 16.5734 4.79424 11.9255 5.081 7.21186C5.23633 4.66686 7.12417 2.98812 9.55565 3.00006C12.1843 3.01799 13.9706 4.92972 13.9288 7.642C13.8391 13.5863 14.0363 19.441 15.3446 25.2658C16.0078 28.223 17.567 32.9068 20.0583 32.9426C20.9126 32.9546 21.8087 32.4647 22.3942 31.6582C25.8711 26.843 26.361 18.515 26.4626 12.3437C26.5223 8.62176 27.1376 4.94764 31.1284 4.76842C32.9684 4.69075 34.6113 5.53311 35.4119 7.26562C36.1049 8.77709 36.2184 10.414 36.2005 12.1167C36.1467 16.4659 36.4215 20.6836 37.3475 24.9074C38.0465 28.0856 39.7432 33.1517 42.6586 32.9486L42.6526 32.9546Z" stroke={LOGO_COLOR} strokeWidth="1" />
            <path data-order="2" d="M100.99 37.6981C101.282 40.0399 99.8844 41.7486 97.7636 42.1369C93.7429 42.8717 90.1943 40.96 89.1309 36.9394C88.414 34.2271 88.2945 31.467 88.3542 28.5636L88.7844 7.39109C88.8142 5.91547 89.0054 4.36816 90.2242 3.41827C91.8909 2.10992 94.7048 2.18759 96.1983 3.72892C97.1841 4.74453 97.3454 6.23808 97.2976 7.62408L96.6165 26.5383C96.5329 28.8682 96.4911 31.0727 96.8435 33.3071C97.2976 36.2165 100.589 34.4183 100.996 37.6981H100.99Z" stroke={LOGO_COLOR} strokeWidth="1" />
            <path data-order="3" d="M115.621 37.7221C115.842 39.5084 115.065 41.1214 113.434 41.7785C111.158 42.6926 108.422 42.3819 106.415 40.9183C103.063 38.4808 102.932 32.9308 103.021 28.5577L103.493 5.90363C103.541 3.73501 105.614 2.60589 107.436 2.51628C110.023 2.39082 112.001 4.08749 111.911 6.78781L111.266 26.3054L111.403 32.9547C111.427 34.263 112.252 35.201 113.56 35.3742C114.695 35.5236 115.466 36.4675 115.615 37.7221H115.621Z" stroke={LOGO_COLOR} strokeWidth="1" />
            <path data-order="4" d="M141.1 21.8898C144.278 19.996 145.945 16.9432 145.778 13.4662C145.664 10.2401 144.027 7.48605 141.333 5.76548C136.548 2.7784 128.68 2.70073 123.171 3.92544C120.901 4.43324 119.055 6.36888 119.037 8.81829L118.822 32.3028L119.234 36.873C119.724 39.412 121.552 41.2282 124.121 41.73C129.14 42.7098 136.004 42.6441 140.7 40.6188C144.9 38.8803 147.445 34.9314 147.289 30.385C147.152 26.4481 144.816 23.2877 141.1 21.8898ZM127.61 11.1661C128.232 10.4851 132.419 10.4373 134.475 11.0228C136.494 11.5963 137.629 13.1615 137.545 15.1688C137.366 19.524 131.667 19.4762 127.108 18.9983L127.067 12.5641C127.067 11.9906 127.335 11.4648 127.61 11.1661ZM139.057 30.9227C138.692 35.2361 133.083 35.4452 128.805 35.0031C127.7 34.8896 127.043 33.9457 127.055 32.8703L127.114 25.9881L132.061 25.8327C133.638 25.7849 135.156 25.8805 136.572 26.5496C138.28 27.3621 139.212 29.0707 139.057 30.9227Z" stroke={LOGO_COLOR} strokeWidth="1" />
            <path data-order="1" d="M83.1568 34.0603C80.3668 32.2202 77.8099 37.1131 71.7879 35.8645C69.5118 35.3925 67.5941 33.8452 67.2476 31.575C73.9566 32.3875 83.9812 32.3696 84.0768 24.7286C84.1186 21.2098 82.1412 18.0913 78.9988 16.4365C75.707 14.704 71.9552 14.3873 68.3588 15.4328C60.7178 17.6552 57.3663 25.7442 59.5588 33.1761C62.277 42.3883 73.3711 44.4852 81.2749 40.7812C82.8521 40.0404 84.0947 38.8038 84.3815 37.137C84.5966 35.8824 84.0947 34.6876 83.1508 34.0663L83.1568 34.0603ZM74.1836 21.2815C75.7608 21.6938 76.9855 23.026 76.5553 24.8123C74.9722 27.0406 70.1152 26.2939 67.0803 25.9952C67.5343 22.339 70.8141 20.4033 74.1836 21.2815Z" stroke={LOGO_COLOR} strokeWidth="1" />
            <path data-order="5" d="M173.659 34.0415C172.715 33.4381 171.467 33.5456 170.38 34.1968C167.709 35.8039 164.573 36.6402 161.484 35.5948C159.501 34.9197 158.192 33.45 157.762 31.5264L159.584 31.7235C165.947 32.4105 174.878 31.7534 174.687 24.5067C174.603 21.3703 173.038 18.5863 170.362 16.8837C164.465 13.1259 155.809 14.6194 151.884 20.6713C149.595 24.196 148.92 28.4735 149.966 32.5479C151.119 37.0345 154.429 40.4099 158.885 41.7123C163.372 43.0207 168.259 42.6861 172.375 40.5175C173.707 39.8185 174.699 38.5759 174.944 37.1958C175.177 35.8815 174.669 34.6986 173.659 34.0475V34.0415ZM163.88 21.1014C165.194 21.2329 166.389 21.7646 166.998 22.9713C167.327 23.6225 167.398 24.3693 166.986 25.0205C165.869 26.8068 160.642 26.3527 157.684 26.0062C158.18 22.7324 160.767 20.7848 163.88 21.0954V21.1014Z" stroke={LOGO_COLOR} strokeWidth="1" />
          </svg>
        </div>
      </div>

      <div className={style['splash-bottom']}>
        <div className={style['mascot-img-box']}>
          <img src={MascotImg} alt="마스코트 이미지" />
        </div>
        <strong>WellBe</strong>
        <p>만성 질환 건강관리 서비스</p>
      </div>
    </div>
  );
});

export default SplashScreen;